import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import { 
  FaShoppingBag, 
  FaGift, 
  FaUserFriends, 
  FaStickyNote 
} from 'react-icons/fa';

const OrderTypeDropdown = ({ 
  selectedType, 
  onTypeChange, 
  isOrderingForOther, 
  isOrderingForExternal, 
  isOrderingByReference 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const orderTypes = [
    {
      value: 'self',
      label: 'Order for Myself',
      description: 'Standard personal order',
      icon: FaShoppingBag,
      color: 'blue',
      isSelected: !isOrderingForOther && !isOrderingForExternal && !isOrderingByReference
    },
    {
      value: 'other',
      label: 'Gift an Order',
      description: 'Send food to a Byte! user',
      icon: FaGift,
      color: 'purple',
      isSelected: isOrderingForOther
    },
    {
      value: 'external',
      label: 'External Order',
      description: 'Send food to anyone',
      icon: FaUserFriends,
      color: 'green',
      isSelected: isOrderingForExternal
    },
    {
      value: 'reference',
      label: 'Reorder',
      description: 'Use a previous order ID',
      icon: FaStickyNote,
      color: 'yellow',
      isSelected: isOrderingByReference
    }
  ];

  const selectedOrderType = orderTypes.find(type => type.isSelected) || orderTypes[0];

  const getColorClasses = (color, isSelected = false, isHover = false) => {
    const colorMap = {
      blue: {
        border: isSelected ? 'border-blue-500' : 'border-gray-200',
        bg: isSelected ? 'bg-blue-50' : isHover ? 'bg-gray-50' : 'bg-white',
        text: isSelected ? 'text-blue-700' : 'text-gray-600',
        icon: isSelected ? 'text-blue-600' : 'text-gray-500',
        iconBg: isSelected ? 'bg-blue-100' : 'bg-gray-100'
      },
      purple: {
        border: isSelected ? 'border-purple-500' : 'border-gray-200',
        bg: isSelected ? 'bg-purple-50' : isHover ? 'bg-gray-50' : 'bg-white',
        text: isSelected ? 'text-purple-700' : 'text-gray-600',
        icon: isSelected ? 'text-purple-600' : 'text-gray-500',
        iconBg: isSelected ? 'bg-purple-100' : 'bg-gray-100'
      },
      green: {
        border: isSelected ? 'border-green-500' : 'border-gray-200',
        bg: isSelected ? 'bg-green-50' : isHover ? 'bg-gray-50' : 'bg-white',
        text: isSelected ? 'text-green-700' : 'text-gray-600',
        icon: isSelected ? 'text-green-600' : 'text-gray-500',
        iconBg: isSelected ? 'bg-green-100' : 'bg-gray-100'
      },
      yellow: {
        border: isSelected ? 'border-yellow-500' : 'border-gray-200',
        bg: isSelected ? 'bg-yellow-50' : isHover ? 'bg-gray-50' : 'bg-white',
        text: isSelected ? 'text-yellow-700' : 'text-gray-600',
        icon: isSelected ? 'text-yellow-600' : 'text-gray-500',
        iconBg: isSelected ? 'bg-yellow-100' : 'bg-gray-100'
      }
    };
    return colorMap[color] || colorMap.blue;
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleTypeSelect = (type) => {
    onTypeChange(type.value);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <h3 className="text-xl font-bold text-crust mb-4 flex items-center gap-2">
        <FaUserFriends className="text-blue-600" />
        How would you like to order?
      </h3>
      
      {/* Dropdown Trigger */}
      <motion.button
        whileHover={{ scale: 1.01 }}
        whileTap={{ scale: 0.99 }}
        onClick={() => setIsOpen(!isOpen)}
        className={`w-full p-4 rounded-xl border-2 transition-all duration-300 ${getColorClasses(selectedOrderType.color, true).border} ${getColorClasses(selectedOrderType.color, true).bg}`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-full ${getColorClasses(selectedOrderType.color, true).iconBg}`}>
              <selectedOrderType.icon className={`w-5 h-5 ${getColorClasses(selectedOrderType.color, true).icon}`} />
            </div>
            <div className="text-left">
              <h4 className={`font-bold ${getColorClasses(selectedOrderType.color, true).text}`}>
                {selectedOrderType.label}
              </h4>
              <p className={`text-sm opacity-75 ${getColorClasses(selectedOrderType.color, true).text}`}>
                {selectedOrderType.description}
              </p>
            </div>
          </div>
          <ChevronDownIcon 
            className={`w-5 h-5 transition-transform duration-200 ${getColorClasses(selectedOrderType.color, true).icon} ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -10, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full left-0 right-0 z-50 mt-2 bg-white border-2 border-gray-200 rounded-xl shadow-xl overflow-hidden"
          >
            {orderTypes.map((type, index) => {
              const IconComponent = type.icon;
              const colors = getColorClasses(type.color, type.isSelected);
              
              return (
                <motion.button
                  key={type.value}
                  whileHover={{ backgroundColor: '#f9fafb' }}
                  onClick={() => handleTypeSelect(type)}
                  className={`w-full p-4 text-left transition-all duration-200 ${colors.bg} ${type.isSelected ? colors.border.replace('border-', 'border-l-4 border-l-') : ''} ${index !== orderTypes.length - 1 ? 'border-b border-gray-100' : ''}`}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${colors.iconBg}`}>
                      <IconComponent className={`w-5 h-5 ${colors.icon}`} />
                    </div>
                    <div className="flex-1">
                      <h4 className={`font-bold ${colors.text}`}>
                        {type.label}
                      </h4>
                      <p className={`text-sm opacity-75 ${colors.text}`}>
                        {type.description}
                      </p>
                    </div>
                    {type.isSelected && (
                      <div className={`w-2 h-2 rounded-full ${colors.icon.replace('text-', 'bg-')}`} />
                    )}
                  </div>
                </motion.button>
              );
            })}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default OrderTypeDropdown;
